import {
  DealItemWithVersions,
  ProductType,
  ValidationRuleId,
} from '@/lib/graphql/generated/graphql';
import { Row } from '@tanstack/react-table';

/** Returns true if validationErrors contain a duplicate error for the given field (gtin or sku). */
const hasDuplicateErrorForField = (
  validationErrors: DealItemWithVersions['activeVersion']['validationErrors'],
  field: 'gtin' | 'sku'
): boolean => {
  if (!validationErrors?.length) return false;
  const ruleId =
    field === 'gtin'
      ? ValidationRuleId.DuplicateGtin
      : ValidationRuleId.DuplicateSku;
  return validationErrors.some((err) => err.ruleId === ruleId);
};

/** i18n key for duplicate error message per field (aligned with backend rules). */
const DUPLICATE_ERROR_MESSAGE_KEY: Record<'gtin' | 'sku', string> = {
  gtin: 'kpi.gtin_duplicate_error',
  sku: 'kpi.sku_duplicate_error',
};

export interface GetTableCellErrorStateProps {
  row: Row<DealItemWithVersions>;
  field: keyof DealItemWithVersions['activeVersion'];
  errorType: 'duplicate' | 'empty' | 'none';
  conditionalLogic?: {
    checkDigitalProduct?: boolean;
  };
}

export interface TableCellErrorState {
  hasError: boolean;
  isEmpty: boolean;
  errorMessage?: string;
}

/**
 * Maps field names to their corresponding error message keys
 */
const getErrorMessageKey = (
  field: keyof DealItemWithVersions['activeVersion']
): string | undefined => {
  const errorMessageMap: Record<string, string> = {
    buyingPrice: 'general.required_price',
    listPrice: 'general.required_price',
    choosePrice: 'general.required_price',
    shippingPrice: 'general.required_price',
    marginPercentage: 'general.required_margin',
    chooseDiscount: 'kpi.choose_discount_required',
  };

  return errorMessageMap[field as string];
};

/**
 * Centralized function for detecting table cell errors
 * Handles duplicate errors (SKU/GTIN), empty state errors, and conditional logic
 * Note: This is NOT a React hook, just a utility function
 *
 * Returns:
 * - hasError: whether to show error styling (takes into account parent/child logic and conditional logic)
 * - isEmpty: whether the cell should display as empty (takes into account parent/child logic)
 * - errorMessage: the i18n key for the error message (if applicable)
 */
export const getTableCellErrorState = ({
  row,
  field,
  errorType,
  conditionalLogic,
}: GetTableCellErrorStateProps): TableCellErrorState => {
  const isParent = row.subRows.length > 1;

  // In "Produit exclus" view we don't show validation errors (aligned with hidden
  // validationErrors column and errorsCount which only counts selected products).
  const isSelected = row.original.activeVersion.isSelected;
  if (!isSelected) {
    return {
      hasError: false,
      isEmpty: false,
      errorMessage: undefined,
    };
  }

  // Handle duplicate errors (GTIN or SKU) – one rule per field, aligned with backend
  if (errorType === 'duplicate' && (field === 'gtin' || field === 'sku')) {
    const getHasDuplicateError = (r: Row<DealItemWithVersions>) =>
      hasDuplicateErrorForField(
        r.original.activeVersion.validationErrors,
        field
      );

    const hasError = isParent
      ? row.subRows.some((subRow) => getHasDuplicateError(subRow))
      : getHasDuplicateError(row);

    return {
      hasError,
      isEmpty: false,
      errorMessage: hasError ? DUPLICATE_ERROR_MESSAGE_KEY[field] : undefined,
    };
  }

  // Handle empty state errors
  if (errorType === 'empty') {
    const getValue = (row: Row<DealItemWithVersions>) =>
      row.original.activeVersion[field];

    // Check if any child is empty (for parent rows)
    const isParentEmpty = isParent
      ? row.subRows.some((subRow) => {
          const value = getValue(subRow);
          return value === null || value === undefined;
        })
      : false;

    // Check if current row is empty
    const value = getValue(row);
    const isEmpty = value === null || value === undefined;

    // For display purposes: isEmpty should be true if current row is empty OR if parent has empty children
    const displayIsEmpty = isParent ? isParentEmpty : isEmpty;

    // Conditional logic for digital products (shippingPrice, madeIn)
    let shouldShowError = isParentEmpty || isEmpty;
    if (conditionalLogic?.checkDigitalProduct) {
      const isDigitalProduct =
        row.original.activeVersion.productType === ProductType.Coupon;
      shouldShowError = shouldShowError && !isDigitalProduct;
    }

    // Get error message key for this field
    const errorMessageKey = getErrorMessageKey(field);

    return {
      hasError: shouldShowError,
      isEmpty: displayIsEmpty,
      errorMessage: shouldShowError ? errorMessageKey : undefined,
    };
  }

  // No error handling
  return {
    hasError: false,
    isEmpty: false,
  };
};
